import React, {
    useMemo,
    useState,
    createContext,
    useEffect,
    useCallback,
} from 'react';
import PropTypes from 'prop-types';
import {
    getInitialContent,
    getLocations,
} from '../../requests/locationRequests';
import i18n from '../../i18n';

export const LocationContext = createContext();

const defaultUniquePlace = {
    display_name: 'Sachsen Center',
    locationLat: '51.104541', // Sachsen Center
    locationLon: '13.201738', // Sachsen Center
    place_id: '9999999',
};

const LocationContextProvider = ({ children }) => {
    const [uniquePlace, setUniquePlace] = useState(defaultUniquePlace);
    const [filterValues, setFilterValues] = useState({
        district: [],
        focalPointOfSupport: [],
        institutions: [],
        koopv: [],
        schoolAccessibility: [],
        publicSchoolAccessibility: [],
        schoolCategory: [],
        schoolCommunity: [],
        schoolInspectorate: [],
        schoolLegalStatus: [],
        schoolLocation: '',
        schoolMeasures: [],
        schoolName: '',
        schoolOwner: [],
        schoolServiceCenter: [],
    });
    const [filters, setFilters] = useState({});
    const [loadingState, setLoadingState] = useState('initial');
    const [errorMessage, setErrorMessage] = useState({ message: '' });
    const [openedLocationId, setOpenedLocationId] = useState('');
    const [openedLocationName, setOpenedLocationName] = useState('');

    const [legend, setLegend] = useState({});
    const [locationObjects, setLocationObjects] = useState([]);
    const [koopvObjects, setKoopvObjects] = useState([]);
    const [currentView, setCurrentView] = useState('map');
    // const [promiseResolved, setPromiseResolved] = useState(false);
    const [resetMap, setResetMap] = useState(0);
    const scrollToMap = useCallback(() => {
        const element = document.getElementById(
            '__interaktive__react__karte__'
        );

        element.scrollIntoView({ behavior: 'smooth' });
    }, []);

    const timeoutPromise = () =>
        new Promise((_, reject) => {
            setTimeout(() => {
                const error = new Error('error');
                error.message = i18n.t('error.timeout');
                reject(error); // Reject with error after timeout
            }, 15000); // 15 seconds
        });

    // INITIAL REQUEST
    useEffect(() => {
        // setErrorMessage('');
        setLoadingState('loading');

        const fetchData = async () => {
            try {
                const response = await Promise.race([
                    getInitialContent(),
                    timeoutPromise(),
                ]);
                if (response?.success) {
                    setKoopvObjects(response.data.entries);
                    setLocationObjects(response.data.entries);
                    setFilters(response.data.filters);
                    setLegend(response.data.legend || {});
                    setLoadingState('success');
                } else {
                    setErrorMessage(i18n.t('error.noSuccess'));
                    setLoadingState('error');
                }
            } catch (error) {
                if (error?.status === 404) {
                    setErrorMessage(i18n.t('error.notFound'));
                    setLoadingState('error');
                }
                if (error?.status === 503) {
                    setErrorMessage(i18n.t('error.timeout'));
                    setLoadingState('error');
                }
                setErrorMessage({ message: String(error?.message || error) });
                setLoadingState('error');
            }
        };

        fetchData();
    }, []);

    // FILTERED REQUEST
    const loadFilteredResults = useCallback(async () => {
        setOpenedLocationId('');
        setOpenedLocationName('');
        setErrorMessage({ message: '' });
        setLoadingState('loading');

        try {
            let activeFilters = 0;
            Object.values(filterValues || {}).forEach((value) => {
                if (value?.length > 0) {
                    activeFilters += 1;
                }
            });

            if (activeFilters > 0) {
                const response = await Promise.race([
                    getLocations(filterValues),
                    timeoutPromise(),
                ]);
                setLocationObjects(response.data.entries);
                setLoadingState('success');
                scrollToMap();
                setResetMap(1 + resetMap);
            }
        } catch (error) {
            setErrorMessage(error?.message);
            setLoadingState('error');
        }
    }, [filterValues, scrollToMap, resetMap]);

    const resetAllFilters = useCallback(() => {
        setResetMap(1 + resetMap);
        setOpenedLocationId('');
        setOpenedLocationName('');

        setFilterValues({
            district: [],
            focalPointOfSupport: [],
            institutions: [],
            koopv: [],
            schoolAccessibility: [],
            publicSchoolAccessibility: [],
            schoolCategory: [],
            schoolCommunity: [],
            schoolInspectorate: [],
            schoolLegalStatus: [],
            schoolLocation: '',
            schoolMeasures: [],
            schoolName: '',
            schoolOwner: [],
            schoolServiceCenter: [],
        });
        setLocationObjects(koopvObjects);
        scrollToMap();
    }, [koopvObjects, scrollToMap, resetMap]);

    const contextProps = useMemo(() => {
        return {
            uniquePlace,
            setUniquePlace,
            legend,
            openedLocationId,
            setOpenedLocationId,
            openedLocationName,
            setOpenedLocationName,
            locationObjects,
            setLocationObjects,
            loadingState,
            currentView,
            setCurrentView,
            filterValues,
            setFilterValues,
            loadFilteredResults,
            filters,
            koopvObjects,
            errorMessage,
            setErrorMessage,
            resetAllFilters,
            resetMap,
            setResetMap,
        };
    }, [
        uniquePlace,
        setUniquePlace,
        legend,
        openedLocationId,
        setOpenedLocationId,
        openedLocationName,
        setOpenedLocationName,
        locationObjects,
        setLocationObjects,
        loadingState,
        currentView,
        setCurrentView,
        filterValues,
        setFilterValues,
        loadFilteredResults,
        filters,
        koopvObjects,
        errorMessage,
        setErrorMessage,
        resetAllFilters,
        resetMap,
    ]);

    return (
        <LocationContext.Provider value={contextProps}>
            {children}
        </LocationContext.Provider>
    );
};

export default LocationContextProvider;

LocationContextProvider.propTypes = {
    children: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.node),
        PropTypes.node,
    ]),
};
